import { TFunction } from 'i18next';
import _ from 'lodash';
import { TwTheme } from '../../../Tw';
import { getBestContrastColor } from '../../common/color.helper';
import { Formatter } from '../../common/useFormatter';
import {
  KPI_TAG_METADATA,
  KPIGroup,
  WarehouseKPI,
} from '../../dashboard/kpi/kpiModel';
import { ActualityAggregatedHqExtendedStatsDataColumn } from './actuality.kpi.types';

// Use the METRIC_TYPE categories from KPI_TAG_METADATA
export type FeedAgentStatsCategory = keyof typeof KPI_TAG_METADATA;

function getActualityAggregationMetricDescriptorsMapRaw(
  t: TFunction<'feed'>,
  formatter: Formatter,
): Record<ActualityAggregatedHqExtendedStatsDataColumn, WarehouseKPI> {
  const descriptorsDict: Record<
    ActualityAggregatedHqExtendedStatsDataColumn,
    WarehouseKPI
  > = {
    jobsCount: {
      id: 'jobsCount',
      title: t('Total Jobs', { ns: 'feed' }),
      legend: t('Jobs', { ns: 'feed' }),
      description: t(
        'Total number of tasks, jobs, work assignments or picklists completed.',
        { ns: 'feed' },
      ),
      category: 'throughput',
      color: [
        TwTheme.extend.colors.heatmap[40],
        TwTheme.extend.colors.text.light,
      ],
      getFormatted: v => formatter.formatInteger(v, t('Jobs', { ns: 'feed' })),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'dimension',
      },
    },
    jobGroupsCount: {
      id: 'jobGroupsCount',
      title: t('Total Orders', { ns: 'feed' }),
      legend: t('Orders', { ns: 'feed' }),
      description: t('Total number of orders processed.', { ns: 'feed' }),
      category: 'throughput',
      color: [
        TwTheme.extend.colors.heatmap[50],
        TwTheme.extend.colors.text.light,
      ],
      getFormatted: v =>
        formatter.formatInteger(v, t('Orders', { ns: 'feed' })),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'dimension',
      },
    },
    eventsCount: {
      id: 'eventsCount',
      title: t('Total Lines', { ns: 'feed' }),
      legend: t('Lines', { ns: 'feed' }),
      description: t('Total number of order lines processed.', { ns: 'feed' }),
      category: 'throughput',
      color: [
        TwTheme.extend.colors.heatmap[60],
        TwTheme.extend.colors.text.light,
      ],
      getFormatted: v => formatter.formatInteger(v, t('Lines', { ns: 'feed' })),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'dimension',
      },
    },
    uomCount: {
      id: 'uomCount',
      title: t('Total Units', { ns: 'feed' }),
      legend: t('Units', { ns: 'feed' }),
      description: t('Total number of units processed.', {
        ns: 'feed',
      }),
      category: 'throughput',
      color: [
        TwTheme.extend.colors.heatmap[70],
        TwTheme.extend.colors.text.light,
      ],
      getFormatted: v => formatter.formatInteger(v, t('Units', { ns: 'feed' })),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    visitsCount: {
      id: 'visitsCount',
      title: t('Total Visits', { ns: 'feed' }),
      legend: t('Visits', { ns: 'feed' }),
      description: t('Total number of visits.', {
        ns: 'feed',
      }),
      category: 'throughput',
      color: [
        TwTheme.extend.colors.heatmap[80],
        TwTheme.extend.colors.text.light,
      ],
      getFormatted: v =>
        formatter.formatInteger(v, t('Visits', { ns: 'feed' })),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    uniqLocationsCount: {
      id: 'uniqLocationsCount',
      title: t('Unique Locations Count', { ns: 'feed' }),
      legend: t('Locations', { ns: 'feed' }),
      description: t('Total number of unique locations visited.', {
        ns: 'feed',
      }),
      category: 'throughput',
      color: [
        TwTheme.extend.colors.heatmap[90],
        TwTheme.extend.colors.text.light,
      ],
      getFormatted: v =>
        formatter.formatInteger(v, t('Locations', { ns: 'feed' })),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },

    shiftsCount: {
      id: 'shiftsCount',
      title: t('Total Shifts', { ns: 'feed' }),
      legend: t('Shifts', { ns: 'feed' }),
      description: t('Total number of shifts.', {
        ns: 'feed',
      }),
      category: 'throughput',
      color: [
        TwTheme.extend.colors.heatmap[80],
        TwTheme.extend.colors.text.light,
      ],
      getFormatted: v =>
        formatter.formatInteger(v, t('Shifts', { ns: 'feed' })),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },

    totalWeight: {
      id: 'totalWeight',
      title: t('Total Weight', { ns: 'feed' }),
      legend: t('Weight', { ns: 'feed' }),
      description: t('', { ns: 'feed' }),
      category: 'throughput',
      color: [
        TwTheme.extend.colors.brand.accent,
        TwTheme.extend.colors.text.light,
      ],
      getFormatted: v => formatter.formatWeight(v),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    totalVolume: {
      id: 'totalVolume',
      title: t('Volume', { ns: 'feed' }),
      legend: t('Volume', { ns: 'feed' }),
      description: t('', { ns: 'feed' }),
      category: 'throughput',
      color: [
        TwTheme.extend.colors.brand.light,
        TwTheme.extend.colors.text.dark,
      ],
      getFormatted: v => formatter.formatVolume(v),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    // Labour time metrics
    totalEmployeeActivityDuration: {
      id: 'totalEmployeeActivityDuration',
      title: t('Worked Time (Net)', { ns: 'feed' }),
      legend: t('Net', { ns: 'feed' }),
      description: t(
        'Total Direct (Net) working time excluding breaks and idle time.',
        { ns: 'feed' },
      ),
      category: 'actual',
      color: [
        TwTheme.extend.colors.eventGroups.labourTotal,
        TwTheme.extend.colors.text.light,
      ],
      getFormatted: formatter.formatDurationHours,
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    idleTimeMs: {
      id: 'idleTimeMs',
      title: t('Idle Time', { ns: 'feed' }),
      legend: t('Idle', { ns: 'feed' }),
      description: t(
        'Total time spent in unknown activities between tasks. (Indirect Time)',
        {
          ns: 'feed',
        },
      ),
      category: 'actual',
      color: [
        TwTheme.extend.colors.eventGroups.idle,
        TwTheme.extend.colors.text.light,
      ],
      getFormatted: formatter.formatDurationHours,
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },

    totalIdleDurationBetweenJobs: {
      id: 'totalIdleDurationShiftStart',
      title: t('Idle Time Between Jobs', { ns: 'feed' }),
      legend: t('Idle', { ns: 'feed' }),
      description: t(
        'Total time spent in unknown activities between tasks. (Indirect Time)',
        {
          ns: 'feed',
        },
      ),
      category: 'actual',
      color: [
        TwTheme.extend.colors.eventGroups.idle,
        TwTheme.extend.colors.text.light,
      ],
      getFormatted: formatter.formatDurationHours,
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    totalIdleDurationShiftEnd: {
      id: 'totalIdleDurationShiftEnd',
      title: t('Idle Time (Shift End)', { ns: 'feed' }),
      description: t(
        'Total time spent in unknown activities at shift end. (Indirect Time)',
        {
          ns: 'feed',
        },
      ),
      category: 'actual',
      color: [
        TwTheme.extend.colors.eventGroups.idle,
        TwTheme.extend.colors.text.light,
      ],
      getFormatted: formatter.formatDurationHours,
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    totalIdleDurationShiftStart: {
      id: 'totalIdleDurationShiftStart',
      title: t('Idle Time (Shift Start)', { ns: 'feed' }),
      description: t(
        'Total time spent in unknown activities at shift start. (Indirect Time)',
        {
          ns: 'feed',
        },
      ),
      category: 'actual',
      color: [
        TwTheme.extend.colors.eventGroups.idle,
        TwTheme.extend.colors.text.light,
      ],
      getFormatted: formatter.formatDurationHours,
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    grossTimeMs: {
      id: 'grossTimeMs',
      title: t('Worked Time (Brutto)', { ns: 'feed' }),
      description: t(
        'Total time including all activities and idle time, measured from first to last event.',
        { ns: 'feed' },
      ),
      category: 'actual',
      color: [
        TwTheme.extend.colors.timeline.selected,
        TwTheme.extend.colors.text.dark,
      ],
      getFormatted: formatter.formatDurationHours,
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },

    shiftActualTime: {
      id: 'shiftActualTime',
      title: t('Shift (Worked Time)', { ns: 'feed' }),
      description: t('Shift (Actual) time spent working during the shift'),
      category: 'labour',
      subcategory: 'actual',
      getFormatted: formatter.formatDurationHours,
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },

    shiftScheduledTime: {
      id: 'shiftScheduledTime',
      title: t('Shift (Planned Time)', { ns: 'feed' }),
      description: t('Total scheduled working hours for all shifts'),
      category: 'labour',
      subcategory: 'actual',
      getFormatted: formatter.formatDurationHours,
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },

    uomLineRatio: {
      id: 'uomLineRatio',
      title: t('Units per Line (Avg. Net)', { ns: 'feed' }),
      description: t('Average number of units processed per job task'),
      category: 'performance',
      getFormatted: v =>
        formatter.formatToPrecision(v, 2, t('units/line', { ns: 'feed' })),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },

    jobLineRatio: {
      id: 'jobLineRatio',
      title: t('Lines per Job', { ns: 'feed' }),
      description: t('Average number of lines processed per job'),
      category: 'performance',
      getFormatted: v =>
        formatter.formatToPrecision(v, 2, t('line/job', { ns: 'feed' })),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },

    orderLineRatio: {
      id: 'orderLineRatio',
      title: t('Lines per Order', { ns: 'feed' }),
      description: t('Average number of lines processed per order'),
      category: 'performance',
      getFormatted: v =>
        formatter.formatToPrecision(v, 2, t('line/job', { ns: 'feed' })),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },

    idleNetRatio: {
      id: 'idleNetRatio',
      title: t('Idle/Net Time Ratio', { ns: 'feed' }),
      legend: t('Idle/Net', { ns: 'feed' }),
      description: t(
        'Ratio of idle time to net (active) working time. Indicates the share of time spent waiting or inactive relative to productive time.',
      ),
      category: 'efficiency',
      getFormatted: formatter.formatShare,
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    netGrossRatio: {
      id: 'netGrossRatio',
      title: t('Net/Gross Time Ratio', { ns: 'feed' }),
      description: t(
        'Ratio of net (active) working time to gross time (including all activities and idle). Indicates the proportion of time spent productively.',
      ),
      category: 'efficiency',
      getFormatted: v => formatter.formatToPrecision(v, 2, ''),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    //activeShiftRatio:{
    //   id: 'activeShiftRatio',
    //   title: t('Net/Actual Shift Time Ratio', { ns: 'feed' }),
    //   description: t(
    //     'Ratio of net (active) working time to actual shift time. Shows how much of the shift was spent on productive work.',
    //   ),
    //   category: 'efficiency',
    //   getFormatted: formatToPrecision,
    // },
    //utilisationScheduledRatio:{
    //   id: 'utilisationScheduledRatio',
    //   title: t('Net/Scheduled Shift Time Ratio', { ns: 'feed' }),
    //   description: t(
    //     'Ratio of net (active) working time to scheduled shift time. Indicates overall utilisation of scheduled hours.',
    //   ),
    //   category: 'efficiency',
    //   getFormatted: formatToPrecision,
    // },
    linesPerVisitedLocation: {
      id: 'linesPerVisitedLocation',
      title: t('Lines per Visited Location', { ns: 'feed' }),
      legend: t('Lines/Location', { ns: 'feed' }),
      description: t(
        'Average number of order lines processed per unique location visited.',
      ),
      category: 'performance',
      getFormatted: v => formatter.formatToPrecision(v, 2, ''),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    uomsPerVisitedLocation: {
      id: 'uomsPerVisitedLocation',
      title: t('Units per Visited Location', { ns: 'feed' }),
      legend: t('Units/Location', { ns: 'feed' }),
      description: t(
        'Average number of units processed per unique location visited.',
      ),
      category: 'performance',
      getFormatted: v => formatter.formatToPrecision(v, 2, ''),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },

    // Productivity
    netLinesPerformance: {
      id: 'netLinesPerformance',
      title: t('Lines / hr (Avg. Net)', { ns: 'feed' }),
      legend: t('Lines/hr', { ns: 'feed' }),
      description: t(
        'Average number of order lines processed per hour of direct (Net) working time (excluding breaks and idle time)',
      ),
      category: 'performance',
      getFormatted: v =>
        formatter.formatToPrecision(v, 2, t('lines/hr', { ns: 'feed' })),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    netUomsPerformance: {
      id: 'netUomsPerformance',
      title: t('Units / hr (Avg. Net)', { ns: 'feed' }),
      legend: t('Units/hr (UPH)', { ns: 'feed' }),
      description: t(
        'Average number of units processed per hour of direct (Net) working time (excluding breaks and idle time)',
      ),
      category: 'performance',
      getFormatted: v =>
        formatter.formatToPrecision(v, 2, t('units/hr', { ns: 'feed' })),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    netJobsPerformance: {
      id: 'netJobsPerformance',
      title: t('Jobs / hr (Avg. Net)', { ns: 'feed' }),
      legend: t('Jobs/hr', { ns: 'feed' }),
      description: t(
        'Average number of orders processed per hour of active working time (excluding breaks and idle time)',
      ),
      category: 'performance',
      getFormatted: v =>
        formatter.formatToPrecision(v, 2, t('jobs/hr', { ns: 'feed' })),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },

    netOrdersPerformance: {
      id: 'netOrdersPerformance',
      title: t('Orders / hr (Avg. Net)', { ns: 'feed' }),
      legend: t('Orders/hr', { ns: 'feed' }),
      description: t(
        'Average number of orders processed per hour of active working time (excluding breaks and idle time)',
      ),
      category: 'performance',
      getFormatted: v =>
        formatter.formatToPrecision(v, 2, t('orders/hr', { ns: 'feed' })),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },

    locationsPerJob: {
      id: 'locationsPerJob',
      title: t('Locations Per Job', { ns: 'feed' }),
      legend: t('Locations/Job', { ns: 'feed' }),
      description: t(''),
      category: 'performance',
      getFormatted: v =>
        formatter.formatToPrecision(v, 2, t('loc/job', { ns: 'feed' })),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    locationsPerOrder: {
      id: 'locationsPerOrder',
      title: t('Locations Per Order', { ns: 'feed' }),
      legend: t('Locations/Order', { ns: 'feed' }),
      description: t(''),
      category: 'performance',
      getFormatted: v =>
        formatter.formatToPrecision(v, 2, t('loc/order', { ns: 'feed' })),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },

    netLocationsPerformance: {
      id: 'netLocationsPerformance',
      title: t('Locations / hr (Avg. Net)', { ns: 'feed' }),
      legend: t('Locations/hr', { ns: 'feed' }),
      description: t(
        'Average number of unique locations visited per hour of active working time (excluding breaks and idle time)',
      ),
      category: 'performance',
      getFormatted: v =>
        formatter.formatToPrecision(v, 2, t('visits/hr', { ns: 'feed' })),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },

    efficiencyHourly: {
      id: 'efficiencyHourly',
      title: t('Locations / hr (Avg. Net)', { ns: 'feed' }),
      legend: t('Locations/hr', { ns: 'feed' }),
      description: t(
        'Average number of unique locations visited per hour of active working time (excluding breaks and idle time)',
      ),
      category: 'performance',
      getFormatted: v =>
        formatter.formatToPrecision(v, 2, t('visits/hr', { ns: 'feed' })),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },

    // {
    //   id: 'efficiencyHourly',
    //   title: t('Efficiency', { ns: 'feed' }),
    //   description: t(
    //     'Efficiency (%) = (Total Units ÷ netTime) ÷ Standard Rate × 100. Paid-Hours = Shift Hours.',
    //     { ns: 'feed' },
    //   ),
    //   category: 'efficiency',
    //   getFormatted: formatToPrecision,
    //   unitOfMeasure: t('%', { ns: 'feed' }),
    // },
    // productivityHoursShift:{
    //   id: 'productivityHoursShift',
    //   title: t('Paid-Hours Productivity', { ns: 'feed' }),
    //   description: t(
    //     'Paid-Hours Productivity (units/hr) = Total Units ÷ Paid-Hours. Paid-Hours = Shift Hours.',
    //     { ns: 'feed' },
    //   ),
    //   category: 'productivity',
    //   getFormatted: formatToPrecision,
    //   unitOfMeasure: t('units/hr', { ns: 'feed' }),
    // },

    grossLinesPerformance: {
      id: 'grossLinesPerformance',
      title: t('Lines / hr (Brutto)', { ns: 'feed' }),
      legend: t('Lines/hr', { ns: 'feed' }),
      description: t('Order lines per hour including all time.', {
        ns: 'feed',
      }),
      category: 'performance',
      subcategory: 'gross',
      getFormatted: v =>
        formatter.formatToPrecision(v, 2, t('lines/hr', { ns: 'feed' })),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    grossUomsPerformance: {
      id: 'grossUomsPerformance',
      title: t('Units / hr (Brutto) (UPH)', { ns: 'feed' }),
      legend: t('Units/hr (UPH)', { ns: 'feed' }),
      description: t('Units per hour including all time.', { ns: 'feed' }),
      category: 'performance',
      subcategory: 'gross',
      tags: ['UPH'],
      getFormatted: v =>
        formatter.formatToPrecision(v, 2, t('units/hr', { ns: 'feed' })),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    grossLocationsPerformance: {
      id: 'grossLocationsPerformance',
      title: t('Locations / hr (Brutto)', { ns: 'feed' }),
      legend: t('Locations/hr', { ns: 'feed' }),
      description: t(
        'Average number of unique locations visited per hour including all time (active, breaks, and idle time)',
      ),
      category: 'performance',
      subcategory: 'gross',
      getFormatted: v =>
        formatter.formatToPrecision(v, 2, t('visits/hr', { ns: 'feed' })),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    grossJobsPerformance: {
      id: 'grossJobsPerformance',
      title: t('Jobs / hr (Brutto)', { ns: 'feed' }),
      legend: t('Jobs/hr', { ns: 'feed' }),
      description: t('Jobs completed per hour including all time.', {
        ns: 'feed',
      }),
      category: 'performance',
      subcategory: 'gross',
      getFormatted: v =>
        formatter.formatToPrecision(v, 2, t('jobs/hr', { ns: 'feed' })),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    grossOrdersPerformance: {
      id: 'grossOrdersPerformance',
      title: t('Orders / hr (Brutto)', { ns: 'feed' }),
      legend: t('Orders/hr', { ns: 'feed' }),
      description: t(
        'Average number of orders processed per hour including all time (active, breaks, and idle time)',
      ),
      category: 'performance',
      subcategory: 'gross',
      getFormatted: v =>
        formatter.formatToPrecision(v, 2, t('orders/hr', { ns: 'feed' })),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },

    // Shift-based performance metrics
    // shiftActualLinesPerformance: {
    //   id: 'shiftActualLinesPerformance',
    //   title: t('Shift (Net) Lines / hr', { ns: 'feed' }),
    //   description: t(
    //     'Average number of order lines processed per hour during actual shift time (excluding scheduled breaks)',
    //   ),
    //   category: 'planned',
    //   subcategory: 'shift',
    //   getFormatted: formatToPrecision,
    //   unitOfMeasure: t('lines/hr', { ns: 'feed' }),
    // },
    // shiftActuaProductivitylUomsPerformance: {
    //   id: 'shiftActuaProductivitylUomsPerformance',
    //   title: t('Shift (Net) Units / hr', { ns: 'feed' }),
    //   description: t(
    //     'Average number of units processed per hour during actual shift time (excluding scheduled breaks)',
    //   ),
    //   category: 'productivity',
    //   subcategory: 'shift',
    //   getFormatted: formatToPrecision,
    //   unitOfMeasure: t('units/hr', { ns: 'feed' }),
    // },
    // shiftActualLocationsPerformance: {
    //   id: 'shiftActualLocationsPerformance',
    //   title: t('Shift (Net) Locations / hr', { ns: 'feed' }),
    //   description: t(
    //     'Average number of unique locations visited per hour during actual shift time (excluding scheduled breaks)',
    //   ),
    //   category: 'productivity',
    //   subcategory: 'shift',
    //   getFormatted: formatToPrecision,
    //   unitOfMeasure: t('visits/hr', { ns: 'feed' }),
    // },
    // shiftActualJobsPerformance: {
    //   id: 'shiftActualJobsPerformance',
    //   title: t('Shift (Net) Jobs / hr', { ns: 'feed' }),
    //   description: t(
    //     'Average number of jobs completed per hour during actual shift time (excluding scheduled breaks)',
    //   ),
    //   category: 'productivity',
    //   subcategory: 'shift',
    //   getFormatted: formatToPrecision,
    //   unitOfMeasure: t('jobs/hr', { ns: 'feed' }),
    // },
    // shiftActualOrdersPerformance: {
    //   id: 'shiftActualOrdersPerformance',
    //   title: t('Shift (Net) Orders / hr', { ns: 'feed' }),
    //   description: t(
    //     'Average number of orders processed per hour during actual shift time (excluding scheduled breaks)',
    //   ),
    //   category: 'productivity',
    //   subcategory: 'shift',
    //   getFormatted: formatToPrecision,
    //   unitOfMeasure: t('orders/hr', { ns: 'feed' }),
    // },
    // shiftScheduledLinesPerformance: {
    //   id: 'shiftScheduledLinesPerformance',
    //   title: t('Shift (Planned) Lines / hr', { ns: 'feed' }),
    //   description: t(
    //     'Average number of order lines processed per hour during scheduled shift time (including scheduled breaks)',
    //   ),
    //   category: 'productivity',
    //   subcategory: 'shift',
    //   getFormatted: formatToPrecision,
    //   unitOfMeasure: t('lines/hr', { ns: 'feed' }),
    // },
    // shiftScheduledUomsPerformance: {
    //   id: 'shiftScheduledUomsPerformance',
    //   title: t('Shift (Planned) Units / hr', { ns: 'feed' }),
    //   description: t(
    //     'Average number of units processed per hour during scheduled shift time (including scheduled breaks)',
    //   ),
    //   category: 'productivity',
    //   subcategory: 'shift',
    //   getFormatted: formatToPrecision,
    //   unitOfMeasure: t('units/hr', { ns: 'feed' }),
    // },
    // shiftScheduledLocationsPerformance: {
    //   id: 'shiftScheduledLocationsPerformance',
    //   title: t('Shift (Planned) Locations / hr', { ns: 'feed' }),
    //   description: t(
    //     'Average number of unique locations visited per hour during scheduled shift time (including scheduled breaks)',
    //   ),
    //   category: 'productivity',
    //   subcategory: 'shift',
    //   getFormatted: formatToPrecision,
    //   unitOfMeasure: t('visits/hr', { ns: 'feed' }),
    // },
    // shiftScheduledJobsPerformance: {
    //   id: 'shiftScheduledJobsPerformance',
    //   title: t('Shift (Planned) Jobs / Hr', { ns: 'feed' }),
    //   description: t(
    //     'Average number of jobs completed per hour during scheduled shift time (including scheduled breaks)',
    //   ),
    //   category: 'productivity',
    //   subcategory: 'shift',
    //   getFormatted: formatToPrecision,
    //   unitOfMeasure: t('jobs/hr', { ns: 'feed' }),
    // },
    // shiftScheduledOrdersPerformance: {
    //   id: 'shiftScheduledOrdersPerformance',
    //   title: t('Shift (Planned) Orders / Hr', {
    //     ns: 'feed',
    //   }),
    //   description: t(
    //     'Average number of orders processed per hour during scheduled shift time (including scheduled breaks)',
    //   ),
    //   category: 'planned',
    //   subcategory: 'productivity',
    //   getFormatted: formatToPrecision,
    //   unitOfMeasure: t('orders/hr', { ns: 'feed' }),
    // },
    perShiftLinesPerformance: {
      id: 'perShiftLinesPerformance',
      title: t('Lines / Shift (Net)', { ns: 'feed' }),
      description: t(
        'Average number of order lines processed per complete shift',
      ),
      category: 'planned',
      subcategory: 'shift',
      getFormatted: v =>
        formatter.formatToPrecision(v, 2, t('lines/shift', { ns: 'feed' })),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    perShiftUomsPerformance: {
      id: 'perShiftUomsPerformance',
      title: t('Units / Shift (Net)', { ns: 'feed' }),
      description: t('Average number of units processed per complete shift'),
      category: 'planned',
      subcategory: 'shift',
      getFormatted: v =>
        formatter.formatToPrecision(v, 2, t('units/shift', { ns: 'feed' })),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    perShiftLocationsPerformance: {
      id: 'perShiftLocationsPerformance',
      title: t('Locations / Shift (Net)', { ns: 'feed' }),
      description: t(
        'Average number of unique locations visited per complete shift',
      ),
      category: 'planned',
      subcategory: 'shift',
      getFormatted: v =>
        formatter.formatToPrecision(v, 2, t('visits/shift', { ns: 'feed' })),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    perShiftJobsPerformance: {
      id: 'perShiftJobsPerformance',
      title: t('Jobs / Shift (Net)', { ns: 'feed' }),
      description: t('Average number of jobs completed per complete shift'),
      category: 'planned',
      subcategory: 'shift',
      getFormatted: v =>
        formatter.formatToPrecision(v, 2, t('jobs/shift', { ns: 'feed' })),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    perShiftOrdersPerformance: {
      id: 'perShiftOrdersPerformance',
      title: t('Orders / Shift (Net)', { ns: 'feed' }),
      description: t('Average number of orders processed per complete shift'),
      category: 'planned',
      subcategory: 'shift',
      getFormatted: v =>
        formatter.formatToPrecision(v, 2, t('orders/shift', { ns: 'feed' })),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    uniqueAgentsCount: {
      id: 'uniqueAgentsCount',
      title: t('Unique Agents', { ns: 'feed' }),
      description: t('Unique agents count'),
      category: 'labour',
      subcategory: 'indirect',
      color: [
        TwTheme.extend.colors.heatmap[90],
        TwTheme.extend.colors.text.light,
      ],
      getFormatted: v =>
        formatter.formatInteger(v, t('Resources', { ns: 'feed' })),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    uniqueUsersCount: {
      id: 'uniqueUsersCount',
      title: t('Unique Users', { ns: 'feed' }),
      description: t('Unique users count'),
      color: [
        TwTheme.extend.colors.heatmap[90],
        TwTheme.extend.colors.text.light,
      ],
      category: 'labour',
      subcategory: 'indirect',
      getFormatted: v => formatter.formatInteger(v, t('Users', { ns: 'feed' })),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    // // #region Custom Events
    // breaks: {
    //   id: 'breaks',
    //   title: t('Breaks', { ns: 'feed' }),
    //   description: t('Total time spent on scheduled and unscheduled breaks'),
    //   category: 'labour',
    //   subcategory: 'indirect',
    //   getFormatted: formatter.formatDurationHours,
    //   recommendedVisualization: {
    //     chartType: ['bar'],
    //     palette: 'elevation-sort',
    //   },
    // },
    // meetings: {
    //   id: 'meetings',
    //   title: t('Meetings', { ns: 'feed' }),
    //   description: t('Total time spent in meetings and team discussions'),
    //   category: 'labour',
    //   subcategory: 'indirect',
    //   getFormatted: formatter.formatDurationHours,
    //   recommendedVisualization: {
    //     chartType: ['bar'],
    //     palette: 'elevation-sort',
    //   },
    // },
    // training: {
    //   id: 'training',
    //   title: t('Training', { ns: 'feed' }),
    //   description: t('Total time spent on training and skill development'),
    //   category: 'labour',
    //   subcategory: 'indirect',
    //   getFormatted: formatter.formatDurationHours,
    //   recommendedVisualization: {
    //     chartType: ['bar'],
    //     palette: 'elevation-sort',
    //   },
    // },
    // recharge: {
    //   id: 'recharge',
    //   title: t('Recharge', { ns: 'feed' }),
    //   description: t('Total time spent on equipment recharging'),
    //   category: 'labour',
    //   subcategory: 'indirect',
    //   getFormatted: formatter.formatDurationHours,
    //   recommendedVisualization: {
    //     chartType: ['bar'],
    //     palette: 'elevation-sort',
    //   },
    // },
    // maintenance: {
    //   id: 'maintenance',
    //   title: t('Maintenance', { ns: 'feed' }),
    //   description: t('Total time spent on equipment maintenance and repairs'),
    //   category: 'labour',
    //   subcategory: 'indirect',
    //   getFormatted: formatter.formatDurationHours,
    //   recommendedVisualization: {
    //     chartType: ['bar'],
    //     palette: 'elevation-sort',
    //   },
    // },
    // misc: {
    //   id: 'misc',
    //   title: t('Misc', { ns: 'feed' }),
    //   description: t(
    //     'Total time spent on miscellaneous activities not categorised elsewhere',
    //   ),
    //   category: 'labour',
    //   subcategory: 'indirect',
    //   getFormatted: formatter.formatDurationHours,
    //   recommendedVisualization: {
    //     chartType: ['bar'],
    //     palette: 'elevation-sort',
    //   },
    // },

    costPerUnit: {
      id: 'costPerUnit',
      title: t('Cost Per Unit', { ns: 'feed' }),
      legend: t('Cost Per Unit', { ns: 'feed' }),
      description: t('Cost per unit of work'),
      category: 'digital_twin_simulation',
      subcategory: 'digital_twin_simulation',
      getFormatted: v => formatter.formatCurrency(v),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    durationRate: {
      id: 'durationRate',
      title: t('Duration Rate', { ns: 'feed' }),
      legend: t('Duration Rate', { ns: 'feed' }),
      description: t('Duration Rate (Actual vs Standard)'),
      category: 'digital_twin_simulation',
      subcategory: 'digital_twin_simulation',
      getFormatted: v => formatter.formatShare(v),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    durationDiff: {
      id: 'durationDiff',
      title: t('Actual vs Standard (Diff)', { ns: 'feed' }),
      legend: t('Actual vs Standard', { ns: 'feed' }),
      description: t(
        'Difference between Actual time and Standard time (simulated duration)',
      ),
      category: 'digital_twin_simulation',
      getFormatted: v => formatter.formatDurationHours(v),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    analyzedCost: {
      id: 'analyzedCost',
      title: t('Total Cost', { ns: 'feed' }),
      legend: t('Cost', { ns: 'feed' }),
      category: 'digital_twin_simulation',
      getFormatted: v => formatter.formatCurrency(v),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    analyzedDuration: {
      id: 'analyzedDuration',
      title: t('Total Time (Duration)', { ns: 'feed' }),
      legend: t('Duration', { ns: 'feed' }),
      category: 'digital_twin_simulation',
      getFormatted: v => formatter.formatDurationHours(v * 1000),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    analyzedDistance: {
      id: 'analyzedDistance',
      title: t('Distance', { ns: 'feed' }),
      legend: t('Distance', { ns: 'feed' }),
      category: 'digital_twin_simulation',
      getFormatted: v => formatter.formatDistance(v, 10),
      color: [
        TwTheme.extend.colors.events.travellingHorizontal,
        getBestContrastColor(TwTheme.extend.colors.events.travellingHorizontal),
      ],
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    analyzedDurationTravellingStart: {
      id: 'analyzedDurationTravellingStart',
      title: t('Travelling Start (Duration)', { ns: 'feed' }),
      legend: t('Travelling Start', { ns: 'feed' }),
      category: 'digital_twin_simulation',
      getFormatted: v => formatter.formatDurationHours(v * 1000),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    analyzedDistanceTravellingStart: {
      id: 'analyzedDistanceTravellingStart',
      title: t('Travelling Start (Distance)', { ns: 'feed' }),
      legend: t('Travelling Start', { ns: 'feed' }),
      category: 'digital_twin_simulation',
      getFormatted: v => formatter.formatDistance(v),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    analyzedCostTravellingStart: {
      id: 'analyzedCostTravellingStart',
      title: t('Travelling Start (Cost)', { ns: 'feed' }),
      legend: t('Travelling Start', { ns: 'feed' }),
      category: 'digital_twin_simulation',
      getFormatted: v => formatter.formatCurrency(v),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    analyzedDurationTravellingEnd: {
      id: 'analyzedDurationTravellingEnd',
      title: t('Travelling End (Duration)', { ns: 'feed' }),
      legend: t('Travelling End', { ns: 'feed' }),
      category: 'digital_twin_simulation',
      getFormatted: v => formatter.formatDurationHours(v * 1000),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    analyzedDistanceTravellingEnd: {
      id: 'analyzedDistanceTravellingEnd',
      title: t('Travelling End (Distance)', { ns: 'feed' }),
      legend: t('Travelling End', { ns: 'feed' }),
      category: 'digital_twin_simulation',
      getFormatted: v => formatter.formatDistance(v),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    analyzedCostTravellingEnd: {
      id: 'analyzedCostTravellingEnd',
      title: t('Travelling End (Cost)', { ns: 'feed' }),
      legend: t('Travelling End', { ns: 'feed' }),
      category: 'digital_twin_simulation',
      getFormatted: v => formatter.formatCurrency(v),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },

    analyzedDurationTravellingInternal: {
      id: 'analyzedDurationTravellingInternal',
      title: t('Travelling (Duration)', { ns: 'feed' }),
      legend: t('Travelling', { ns: 'feed' }),
      category: 'digital_twin_simulation',
      getFormatted: v => formatter.formatDurationHours(v * 1000),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    analyzedDistanceTravellingInternal: {
      id: 'analyzedDistanceTravellingInternal',
      title: t('Travelling (Distance)', { ns: 'feed' }),
      legend: t('Travelling', { ns: 'feed' }),
      category: 'digital_twin_simulation',
      getFormatted: v => formatter.formatDistance(v),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    analyzedCostTravellingInternal: {
      id: 'analyzedCostTravellingInternal',
      title: t('Travelling (Cost)', { ns: 'feed' }),
      legend: t('Travelling', { ns: 'feed' }),
      category: 'digital_twin_simulation',
      getFormatted: v => formatter.formatCurrency(v),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },

    analyzedDurationTravellingVertical: {
      id: 'analyzedDurationTravellingVertical',
      title: t('Travelling (Up/Down) ....', { ns: 'feed' }),
      legend: t('Travelling', { ns: 'feed' }),
      category: 'digital_twin_simulation',
      getFormatted: v => formatter.formatDurationHours(v * 1000),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    analyzedCostTravellingVertical: {
      id: 'analyzedCostTravellingVertical',
      title: t('Travelling (Up/Down)', { ns: 'feed' }),
      legend: t('Travelling', { ns: 'feed' }),
      category: 'digital_twin_simulation',
      getFormatted: v => formatter.formatCurrency(v),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    analyzedDistanceTravellingVertical: {
      id: 'analyzedDistanceTravellingVertical',
      title: t('Travelling (Up/Down)', { ns: 'feed' }),
      legend: t('Travelling', { ns: 'feed' }),
      category: 'digital_twin_simulation',
      getFormatted: v => formatter.formatDistance(v),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    // analyzedVolume: {
    //   id: 'analyzedVolume',
    //   title: t('Analyzed Volume', { ns: 'feed' }),
    //   category: 'digital_twin_simulation',
    //   getFormatted: v => formatter.formatVolume(v).fullString,
    // },
    // analyzedWeight: {
    //   id: 'analyzedWeight',
    //   title: t('Analyzed Weight', { ns: 'feed' }),
    //   category: 'digital_twin_simulation',
    //   getFormatted: v => formatter.formatWeight(v).fullString,
    // },
    analyzedDurationHandling: {
      id: 'analyzedDurationHandling',
      title: t('Handling (Duration)', { ns: 'feed' }),
      legend: t('Handling', { ns: 'feed' }),
      category: 'digital_twin_simulation',
      getFormatted: v => formatter.formatDurationHours(v * 1000),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    analyzedCostHandling: {
      id: 'analyzedCostHandling',
      title: t('Handling (Cost)', { ns: 'feed' }),
      legend: t('Handling', { ns: 'feed' }),
      category: 'digital_twin_simulation',
      getFormatted: v => formatter.formatCurrency(v),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    analyzedDurationPicking: {
      id: 'analyzedDurationPicking',
      title: t('Picking (Duration)', { ns: 'feed' }),
      legend: t('Picking', { ns: 'feed' }),
      category: 'digital_twin_simulation',
      getFormatted: v => formatter.formatDurationHours(v * 1000),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    analyzedCostPicking: {
      id: 'analyzedCostPicking',
      title: t('Picking (Cost)', { ns: 'feed' }),
      legend: t('Picking', { ns: 'feed' }),
      category: 'digital_twin_simulation',
      getFormatted: v => formatter.formatCurrency(v),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },

    analyzedDurationReordering: {
      id: 'analyzedDurationReordering',
      title: t('Reordering (Duration)', { ns: 'feed' }),
      legend: t('Reordering', { ns: 'feed' }),
      category: 'digital_twin_simulation',
      getFormatted: v => formatter.formatTimespanAs24String(v),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    analyzedCostReordering: {
      id: 'analyzedCostReordering',
      title: t('Reordering (Cost)', { ns: 'feed' }),
      legend: t('Reordering', { ns: 'feed' }),
      category: 'digital_twin_simulation',
      getFormatted: v => formatter.formatCurrency(v),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },

    totalDistancePerLine: {
      id: 'totalDistancePerLine',
      title: t('Distance Per Line', { ns: 'feed' }),
      legend: t('Distance', { ns: 'feed' }),
      description: t('Total distance traveled by the resource'),
      category: 'digital_twin_simulation',
      getFormatted: v => formatter.formatDistance(v),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    totalDistancePerOrder: {
      id: 'totalDistancePerOrder',
      title: t('Distance Per Order', { ns: 'feed' }),
      legend: t('Distance/Order', { ns: 'feed' }),
      description: t('Total distance traveled by the resource per each Order'),
      category: 'digital_twin_simulation',
      getFormatted: v => formatter.formatDistance(v),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    totalDistancePerJob: {
      id: 'totalDistancePerJob',
      title: t('Total Distance Per Job', { ns: 'feed' }),
      legend: t('Distance/Job', { ns: 'feed' }),
      description: t('Total distance traveled by the resource per each Job'),
      category: 'digital_twin_simulation',
      getFormatted: v => formatter.formatDistance(v),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    internalDistancePerLine: {
      id: 'internalDistancePerLine',
      title: t('Internal Distance Per Line', { ns: 'feed' }),
      legend: t('Distance/Line', { ns: 'feed' }),
      description: t('Internal distance traveled by the resource'),
      category: 'digital_twin_simulation',
      getFormatted: v => formatter.formatDistance(v),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    internalDistancePerOrder: {
      id: 'internalDistancePerOrder',
      title: t('Internal Distance Per Order', { ns: 'feed' }),
      legend: t('Distance/Order', { ns: 'feed' }),
      description: t(
        'Internal distance traveled by the resource per each Order',
      ),
      category: 'digital_twin_simulation',
      getFormatted: v => formatter.formatDistance(v),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },
    internalDistancePerJob: {
      id: 'internalDistancePerJob',
      title: t('Internal Distance Per Job', { ns: 'feed' }),
      legend: t('Distance/Job', { ns: 'feed' }),
      description: t('Internal distance traveled by the resource per each Job'),
      category: 'digital_twin_simulation',
      getFormatted: v => formatter.formatDistance(v),
      recommendedVisualization: {
        chartType: ['bar'],
        palette: 'elevation-sort',
      },
    },

    // Efficiency metrics
    // standardRate: {
    //   id: 'standardRate',
    //   title: t('Standard Rate', { ns: 'feed' }),
    //   description: t(
    //     'Target units per hour from MTM/time studies/SLA. Used as the baseline for efficiency calculations. Currently using secondsOperation as the standard rate.',
    //     { ns: 'feed' },
    //   ),
    //   category: 'efficiency',
    //   subcategory: 'efficiency',
    //   getFormatted: formatToPrecision,
    //   unitOfMeasure: t('units/hr', { ns: 'feed' }),
    // },
    // workHoursProductivity: {
    //   id: 'workHoursProductivity',
    //   title: t('Work-Hours Productivity', { ns: 'feed' }),
    //   description: t(
    //     'Units picked per net working hour. Calculation: Total Units ÷ Net Time Hours. Net Time excludes breaks and idle time.',
    //     { ns: 'feed' },
    //   ),
    //   category: 'efficiency',
    //   subcategory: 'efficiency',
    //   getFormatted: formatToPrecision,
    //   unitOfMeasure: t('units/hr', { ns: 'feed' }),
    // },
    // efficiencyOutputRate: {
    //   id: 'efficiencyOutputRate',
    //   title: t('Efficiency (Output Rate)', { ns: 'feed' }),
    //   description: t(
    //     'Output-rate method efficiency. Calculation: (Work-Hours Productivity ÷ Standard Rate) × 100. Shows how actual productivity compares to the standard rate.',
    //     { ns: 'feed' },
    //   ),
    //   category: 'efficiency',
    //   subcategory: 'efficiency',
    //   getFormatted: formatToPrecision,
    //   unitOfMeasure: '%',
    // },
    // efficiencyShiftHours: {
    //   id: 'efficiencyShiftHours',
    //   title: t('Efficiency (Shift Hours)', { ns: 'feed' }),
    //   description: t(
    //     'Standard-hours method efficiency. Calculation: (Shift (Planned) Hours ÷ Net (Direct) Hours) × 100. Shows how actual time compares to Shift Planned time.',
    //     { ns: 'feed' },
    //   ),
    //   category: 'efficiency',
    //   subcategory: 'efficiency',
    //   getFormatted: (v: number) => formatShare(v, t).fullString,
    //   unitOfMeasure: '%',
    // },
    // standardHours: {
    //   id: 'standardHours',
    //   title: t('Standard Hours', { ns: 'feed' }),
    //   description: t(
    //     'Expected time to complete the work. Calculation: Total Units ÷ Standard Rate. Represents how long the work should take at the standard rate.',
    //     { ns: 'feed' },
    //   ),
    //   category: 'efficiency',
    //   subcategory: 'efficiency',
    //   getFormatted: formatToPrecision,
    //   unitOfMeasure: t('hours', { ns: 'feed' }),
    // },

    // fullTimeEquivalents: {
    //   id: 'fullTimeEquivalents',
    //   title: t('FTEs', { ns: 'feed' }),
    //   description: t(
    //     'Full-Time Equivalents. Calculation: Shift Planned ÷ Actual Hours. Shows how many full-time workers would be needed to cover the scheduled time.',
    //     { ns: 'feed' },
    //   ),
    //   category: 'labour',
    //   subcategory: 'efficiency',
    //   getFormatted: formatToPrecision,
    //   unitOfMeasure: 'FTE',
    // },
  };

  return descriptorsDict;
}

export const getActualityAggregationMetricDescriptorsMap = _.memoize(
  getActualityAggregationMetricDescriptorsMapRaw,
);

export function getActualityAggregationMetricDescriptors(
  t: TFunction<'feed'>,
  formatter: Formatter,
): WarehouseKPI[] {
  return _.values(getActualityAggregationMetricDescriptorsMap(t, formatter));
}

export function getFeedAgentStatsDescByCategory(
  t: TFunction<'feed'>,
  formatter: Formatter,
) {
  return _.groupBy(
    getActualityAggregationMetricDescriptors(t, formatter),
    'category',
  ) as Record<KPIGroup, WarehouseKPI[]>;
}
